import dynamic from "next/dynamic";

import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { useOnClickOutside } from "usehooks-ts";

import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";

import { SKSpinner } from "components/customElements/skSpinner";
import FontAwesomeIcon from "components/FontAwesomeIcon";

const SearchResults = dynamic(() => import("./SearchResults"), { ssr: false });
const SearchSnippet = dynamic(() => import("./SearchSnippet"), { ssr: false });

export type Ref = { focus?: () => void };

type SearchInputProps = {
  className?: string;
  inputClassName?: (_isFocusing: boolean) => string;
  iconClassName?: (_isFocusing: boolean) => string;
  dropdownClassName?: string;
  icon?: boolean;
  endpoint: string;
  queryParam: string;
  additionalParams?: { [key: string]: string } | string;
  onItemSelected?: (_item: any) => void;
  renderItem?: (_item: any) => React.ReactNode;
  customHistoriesHeader?: React.ReactNode;
  histories?: React.ReactNode | any[];
  isHistoriesLoading?: boolean;
  placeholder?: string;
  resultPlaceholder?: string;
  showSearchFor?: boolean;
  customListHeader?: (_props: {
    query: string;
    onItemClick: (_item: any) => void;
  }) => React.ReactNode;
  disableEmptyMessage?: boolean;
  autoFocus?: boolean;
  autoFocusWithHistories?: boolean;
  isEmpty?: (_params: { response: any }) => boolean;
  customEmptyMessage?: (_props: {
    query: string;
    response: any;
  }) => React.ReactChild;
  processData?: (_data: { data: any }) => { data: any };
  searchListClassName?: string;
  // Seeds the input on focus so an existing free-text value can be edited
  // instead of retyped. Cleared again on blur, like any other query.
  editableValue?: string;
};

const SearchInput = (props: SearchInputProps, ref: ForwardedRef<Ref>) => {
  const containerElement = useRef<HTMLInputElement>(null);
  const searchElement = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isFocusing, setIsFocusing] = useState<boolean>(!!props.autoFocus);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, _setActiveIndex] = useState(-1);
  const [enterTrigger, setEnterTrigger] = useState(0);
  const activeIndexRef = useRef(-1);
  const totalItemsRef = useRef(0);

  const setActiveIndex = (val: number) => {
    activeIndexRef.current = val;
    _setActiveIndex(val);
  };

  const onFocus = useCallback(() => {
    setIsFocusing(true);
    const editable = props.editableValue;
    if (editable && !query) {
      setQuery(editable);
      // The value lands on the input after this render, so move the caret to
      // the end once it is there rather than leaving it wherever the click fell.
      requestAnimationFrame(() => {
        searchElement.current?.setSelectionRange(
          editable.length,
          editable.length
        );
      });
    }
  }, [props.editableValue, query]);

  const onBlur = useCallback(() => {
    setQuery("");
    setIsFocusing(false);
    activeIndexRef.current = -1;
    _setActiveIndex(-1);
  }, []);

  const onItemClick = useCallback(
    (data) => {
      onBlur();
      props.onItemSelected && props.onItemSelected(data);
    },
    [onBlur, props]
  );

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    activeIndexRef.current = -1;
    _setActiveIndex(-1);
  }, []);

  const onNavItemsChange = useCallback((count: number) => {
    totalItemsRef.current = count;
    activeIndexRef.current = -1;
    _setActiveIndex(-1);
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isFocusing) return;
    const isSearchMode = !!(
      query ||
      (props.autoFocus && !props.autoFocusWithHistories)
    );
    const total = isSearchMode
      ? totalItemsRef.current
      : Array.isArray(props.histories)
      ? (props.histories as any[]).length
      : 0;

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (total === 0) return;
      e.preventDefault();
      if (e.key === "ArrowDown") {
        const next =
          activeIndexRef.current < 0 ? 0 : (activeIndexRef.current + 1) % total;
        setActiveIndex(next);
      } else {
        const next =
          activeIndexRef.current <= 0 ? total - 1 : activeIndexRef.current - 1;
        setActiveIndex(next);
      }
    } else if (e.key === "Enter" && activeIndexRef.current >= 0) {
      e.preventDefault();
      if (isSearchMode) {
        setEnterTrigger((t) => t + 1);
      } else if (Array.isArray(props.histories)) {
        const item = (props.histories as any[])[activeIndexRef.current];
        if (item) onItemClick(item);
      }
    }
  };

  useOnClickOutside(containerElement, onBlur, "mouseup");

  useEffect(() => {
    if (activeIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.querySelector(
        '[data-active="true"]'
      );
      activeEl?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  useEffect(() => {
    if (props.autoFocus) searchElement.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    focus() {
      searchElement.current?.focus();
    },
  }));

  return (
    <div
      className={classNames("relative", props.className)}
      ref={containerElement}
    >
      <div className={classNames("relative", "flex items-center")}>
        {props.icon && (
          <FontAwesomeIcon
            className={classNames(
              "absolute mx-[11.5px]",
              "text-[15px]",
              props.iconClassName?.(isFocusing)
            )}
            icon={faSearch}
          />
        )}
        <input
          className={twMerge(
            "w-full h-[33px] text-[16px] md:text-[15px]",
            "outline-none border border-solid",
            "bg-white ",
            props.icon ? "indent-[38px]" : "indent-[10px]",
            isFocusing
              ? "rounded-t-sm border-b-0 border-smart-teal"
              : "rounded-sm border-[#ccc]",
            props.inputClassName?.(isFocusing)
          )}
          autoFocus={props.autoFocus}
          ref={searchElement}
          placeholder={props.placeholder || "Search"}
          value={query}
          onFocus={onFocus}
          onChange={onChange}
          onKeyDown={onKeyDown}
        />
        <FontAwesomeIcon
          className={classNames(
            "animate-spin absolute right-[12.5px]",
            loading ? "" : "hidden"
          )}
          icon={faSpinner}
        />
      </div>
      {isFocusing && (
        <>
          <div
            ref={dropdownRef}
            className={twMerge(
              "absolute left-0 right-0 top-[30px] p-[4px]",
              "bg-white shadow rounded-b",
              "border border-t-0 border-solid border-smart-teal",
              "z-10 max-h-[300px] overflow-y-auto",
              props.dropdownClassName,
              props.searchListClassName
            )}
          >
            {query || (props.autoFocus && !props.autoFocusWithHistories) ? (
              <>
                <SearchResults
                  {...props}
                  query={query}
                  onItemClick={onItemClick}
                  onLoading={setLoading}
                  showSearchFor={props.showSearchFor}
                  activeIndex={activeIndex}
                  onNavItemsChange={onNavItemsChange}
                  enterTrigger={enterTrigger}
                />
              </>
            ) : props.histories || props.isHistoriesLoading ? (
              <div className="">
                {props.customHistoriesHeader || (
                  <div className="bg-[#eee] px-[7px] py-[3px]">
                    Recent Search History
                  </div>
                )}
                {props.isHistoriesLoading ? (
                  <div className="flex justify-center">
                    <SKSpinner />
                  </div>
                ) : (
                  <div>
                    {Array.isArray(props.histories)
                      ? props.histories.map((item, i) => (
                          <SearchSnippet
                            key={item.id}
                            data={item}
                            isActive={activeIndex === i}
                            onClick={() => onItemClick(item)}
                          />
                        ))
                      : props.histories}
                  </div>
                )}
              </div>
            ) : (
              props.resultPlaceholder && (
                <div className="">
                  <div className="bg-[#eee] px-[7px] py-[3px]">
                    {props.resultPlaceholder}
                  </div>
                </div>
              )
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default forwardRef<Ref, SearchInputProps>(SearchInput);
